/**
 * Book detail — admin page. Shows one book's registry metadata plus the list of
 * questions that reference it (textbook origin, via `bookSources[].bookId`).
 * Reached by clicking a row on the Books list. Each question row deep-links into
 * the Questions page (?id=<qid>) where it can be viewed/edited.
 *
 * The question list is server-paginated (a book can reference more than the
 * backend's 200-row page cap): search is sent to the server, and column sort is
 * applied to the current page. See docs/FeatureDesigns/question-pools-and-entitlement/question-source-type-target.md.
 */
import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import type { Book, QuestionListItem } from '../types';
import { LatexText } from '@shared-ui/latex';
import { DataTable, type ColumnDef, type SortState } from '@shared-ui/components';
import './Books.css';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Small labelled metadata pill for the book header. */
function Meta({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div style={{ fontSize: 'var(--font-xs)' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{label}: </span>
      <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
    </div>
  );
}

/** Sort key → comparable string for a question row. */
function sortValue(q: QuestionListItem, key: string): string {
  switch (key) {
    case 'questionType': return (q.questionType || '').toLowerCase();
    case 'importStatus': return (q.importStatus || '').toLowerCase();
    case 'module': return (q.module?.name || '').toLowerCase();
    case 'questionText':
    default: return (q.questionText || '').toLowerCase();
  }
}

export function BookDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ sortBy: 'questionText', sortOrder: 'asc' });

  const { data: book, isLoading: bookLoading, error: bookError } =
    useQuery<Book>({ queryKey: ['book', id], queryFn: () => adminApi.getBook(id), enabled: !!id });

  // Server-paginated: bookId + optional searchString, one page at a time. The
  // backend caps a page at 200 rows and returns the true `total`, so paging is
  // the only way to see a book with >200 questions.
  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: ['book-questions', id, page, search],
    queryFn: () => adminApi.getQuestionList({
      bookId: id,
      page,
      pageSize: PAGE_SIZE,
      ...(search ? { searchString: search } : {}),
    }),
    enabled: !!id,
    placeholderData: keepPreviousData,   // keep the current page visible while the next loads
  });
  const questions = useMemo(() => questionsData?.questions ?? [], [questionsData]);
  const total = questionsData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Column sort applies to the current page only (the backend orders by
  // questionText; custom column ordering isn't a server capability here).
  const visible = useMemo(() => {
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return [...questions].sort((a, b) =>
      sortValue(a, sort.sortBy).localeCompare(sortValue(b, sort.sortBy)) * dir);
  }, [questions, sort]);

  // Server search: reset to page 1 on a new query (fires on Enter / Fetch).
  const handleSearch = (q: string) => { setSearch(q.trim()); setPage(1); };

  const back = <Link to="/books" style={{ fontSize: 'var(--font-xs)' }}>← Books</Link>;

  const columns: ColumnDef<QuestionListItem>[] = [
    {
      key: 'questionText', header: 'Question', sortable: true,
      render: q => <LatexText text={q.questionText || '—'} truncate={140} />,
    },
    { key: 'questionType', header: 'Type', width: '1%', sortable: true, render: q => q.questionType || '—' },
    {
      key: 'importStatus', header: 'Status', width: '1%', sortable: true,
      render: q => (
        <span style={{
          fontSize: 'var(--font-xs)', fontWeight: 600, whiteSpace: 'nowrap',
          color: q.importStatus === 'live' ? 'var(--color-success, #16a34a)' : 'var(--text-secondary)',
        }}>
          {q.importStatus === 'live' ? 'Live' : 'Draft'}
        </span>
      ),
    },
    { key: 'module', header: 'Module', sortable: true, render: q => q.module?.name || '—' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 12 }}>{back}</div>

      {bookError && <div style={{ color: 'var(--color-error)' }}>{(bookError as Error).message}</div>}

      {book && (
        <>
          <h1 style={{ margin: '4px 0 8px', color: 'var(--text-primary)' }}>{book.title}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <Meta label="Author" value={book.author} />
            <Meta label="Publisher" value={book.publisher} />
            <Meta label="Subject" value={book.subject ? cap(book.subject) : undefined} />
            <Meta label="Board" value={book.board} />
            <Meta label="Class" value={book.grades?.length ? book.grades.join(', ') : undefined} />
            <Meta label="Edition" value={book.edition} />
          </div>
        </>
      )}

      <h2 style={{ fontSize: 'var(--font-md)', fontWeight: 600, margin: '0 0 6px', color: 'var(--text-primary)' }}>
        Questions{total ? ` (${total})` : ''}
      </h2>
      <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 'var(--font-xs)', maxWidth: 620 }}>
        Questions that reference this book as their textbook source. Click a row to open it in the Questions page.
      </p>

      <DataTable<QuestionListItem>
        columns={columns}
        data={visible}
        rowKey={q => q.id}
        onRowClick={q => navigate(`/questions?id=${encodeURIComponent(q.id)}`)}
        sort={sort}
        onSortChange={setSort}
        onSearch={handleSearch}
        searchPlaceholder="Search questions…"
        pagination={{ page, totalPages, total, onPageChange: setPage }}
        loading={bookLoading || questionsLoading}
        bordered
        emptyMessage={search ? 'No questions match your search.' : 'No questions reference this book yet.'}
      />
    </div>
  );
}
